<template>
  <profile-modal-base
    :is-open="showModal"
    data-test="update-credit-card-modal"
    @close="handleClose"
  >
    <template #header>
      <profile-modal-header
        :header-text="'Update Payment Info'"
        :back-target="'accountModal'"
      ></profile-modal-header>
    </template>
    <div class="wrapper">
      <fieldset>
        <label class="text-sm font-medium text-secondary">Cardholder Name</label>
        <input
          v-model="cardholderName"
          type="text"
          placeholder="name"
          data-test="cardholder-name"
          class="input-text"
        />
      </fieldset>
      <fieldset>
        <label class="text-sm font-medium text-secondary" for="stripe-elements">Payment Information</label>
        <div id="stripe-elements" />
        <div
          v-if="errorMessage.length > 0"
          class="errors"
        >
          <icon
            name="warning"
            variant="dc3416"
          />
          <p>{{ errorMessage }}</p>
        </div>
      </fieldset>
    </div>
    <span class="flex-1"></span>
    <div class="button-row">
      <button class="button-reset button-basic" @click="back">
        Cancel
      </button>
      <button class="button-reset button-primary font-semibold" @click="handleSubmission">
        Update Payment
      </button>
    </div>
  </profile-modal-base>
</template>

<script>
import Icon from '@/components/singletons/icon';
import ProfileModalBase from './ProfileModalBase.vue';
import ProfileModalHeader from './ProfileModalHeader.vue';
import ProfileMixin from '@/mixins/profile';
import ProfileModalMixin from '@/mixins/profileModal';
import * as Sentry from '@sentry/browser';

const MODAL_NAME = 'updatePaymentInfoModal';
let card;
let stripe;

export default {
  name: 'UpdatePaymentInfoModal',
  components: {
    Icon,
    ProfileModalBase,
    ProfileModalHeader,
  },
  mixins: [ProfileModalMixin, ProfileMixin],
  data() {
    return {
      cardholderName: '',
      errorMessage: '',
      submitting: false,
    };
  },
  computed: {
    showModal() {
      return this.$store.getters['ui/profileModalShown'] === MODAL_NAME;
    },
  },
  watch: {
    async showModal(newVal) {
      if (newVal) {
        await this.$nextTick();
        this.loadElements();
      } else {
        this.clearState();
      }
    },
  },
  created() {
    this.loadStripe();
  },
  methods: {
    back() {
      this.$store.commit('ui/setProfileModalShown', 'accountModal');
    },
    clearState() {
      this.errorMessage = '';
      this.cardholderName = '';
      this.submitting = false;
    },
    handleClose() {
      this.clearState();
      this.close();
    },
    handleSubmission() {
      if (this.cardholderName.length < 1) {
        this.errorMessage = 'Your name cannot be blank.';
        return;
      }
      this.submitting = true;
      this.$analytics.events.savedCreditCard();
      stripe.createSource(card, { owner: { name: this.cardholderName }}).then(resp => {
        if (resp.error) {
          if (resp.error.type === 'validation_error') {
            this.errorMessage = resp.error.message;
          } else {
            // Won't actually throw an error but we should make sure it gets sent to Sentry
            Sentry.captureMessage(JSON.stringify(resp.error));
            this.errorMessage = 'Uh-oh! Something went wrong. Contact support if this continues.';
          }
          this.submitting = false;
        } else {
          let notification;
          this.addCard(resp.source.id)
            .then((cards) => {
              this.$emit('set-cards', cards);
              notification = {
                group: 'notifications',
                text: 'Payment method updated successfully',
                type: 'success',
              };
              this.back();
            }).catch(() => {
              notification = {
                group: 'notifications',
                text: 'Error occurred when saving payment method. Contact support if this continues',
                type: 'error',
              };
            }).finally(() => {
              this.submitting = false;
              this.$notify(notification);
            });
      }
      }).catch(() => {
        this.errorMessage = 'Uh-oh! Something went wrong. Contact support if this continues.';
        this.submitting = false;
      });
    },
    loadElements() {
      if (window.Stripe) {
        stripe = window.Stripe(process.env.STRIPE_TOKEN);
        const elements = stripe.elements();

        card = elements.create('card');
        card.mount('#stripe-elements');
        card.on('change', (event) => {
          if (event.error) {
            this.errorMessage = event.error.message;
          } else {
            this.errorMessage = '';
          }
        });
      } else {
        Sentry.captureMessage('Stripe Elements failed to load: missing Stripe on window');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  margin-top: 1rem;
}

.button-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  gap: rem-calc(12);
}

fieldset {
  border: none;
  display: flex;
  flex-direction: column;
}

#stripe-elements,
.input-text {
  border: none;
    padding: 0.75rem;
    background-color: white;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-primary);
    margin-top: rem-calc(6);
    font-size: 1rem;
    color: #333;

    &:focus {
      outline: none;
      box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
    }
}
</style>
